// Archivo: src/features/rewards/components/RewardForm.tsx

import React, { useState, useEffect } from 'react';
import { Save, Loader2, DollarSign, Star, MapPin } from 'lucide-react'; // ✅ Nuevos iconos
import type { RewardFormData } from '../types/reward';
import ImageUpload from '../../../components/common/ImageUpload';
import { useAuth } from '../../../context/AuthContext'; // ✅ Importar contexto
import { toast } from 'react-hot-toast';

interface RewardFormProps {
  initialData?: RewardFormData;
  onSubmit: (data: RewardFormData) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

const RewardForm: React.FC<RewardFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading
}) => {
  // ✅ 1. Obtener el modo de lealtad
  const { currentUser } = useAuth();
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';
  // Recompensas por sucursal (10-oct-2026): solo con 2+ sucursales activas. [] = todas.
  const sucursales = (currentUser?.branches || []).filter((b) => b.isActive !== false);
  const [soloAlgunas, setSoloAlgunas] = useState<boolean>(!!(initialData?.branchIds && initialData.branchIds.length));
  useEffect(() => { setSoloAlgunas(!!(initialData?.branchIds && initialData.branchIds.length)); }, [initialData]);
  const toggleSucursal = (id: string) => setFormData((prev) => {
    const actual = prev.branchIds || [];
    return { ...prev, branchIds: actual.includes(id) ? actual.filter((x) => x !== id) : [...actual, id] };
  });

  const [formData, setFormData] = useState<RewardFormData>(initialData || {
    title: '',
    description: '',
    points: 0,
    imageUrl: '',
    isActive: true
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({ title: '', description: '', points: 0, imageUrl: '', isActive: true });
    }
  }, [initialData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // ✅ Permitir decimales si es 'cashback'
    if (name === 'points') {
        const numValue = parseFloat(value);
        setFormData(prev => ({ ...prev, [name]: isNaN(numValue) ? 0 : numValue }));
    } else {
        setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseInt(value) || 0 : value }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error('El título es requerido'); return; }
    if (!formData.imageUrl) { toast.error('La imagen es requerida'); return; }
    
    // Validación genérica
    if (formData.points <= 0) { 
        toast.error(loyaltyMode === 'cashback' ? 'El costo debe ser mayor a $0.00' : 'Los puntos requeridos deben ser mayores a 0'); 
        return; 
    }

    if (sucursales.length >= 2 && soloAlgunas && !(formData.branchIds || []).length) {
        toast.error('Elige al menos una sucursal, o marca «Todas las sucursales»');
        return;
    }
    // Con 2+ sucursales siempre se manda la lista ([] = todas); con 1 no se toca.
    const branchIds = sucursales.length >= 2 ? (soloAlgunas ? (formData.branchIds || []) : []) : undefined;
    await onSubmit({ ...formData, branchIds });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div> 
          <label htmlFor="title" className="block text-sm font-medium text-content-2">Título</label> 
          <input type="text" id="title" name="title" value={formData.title} onChange={handleInputChange} required className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm" placeholder="Ej. Café Americano Gratis" /> 
      </div>
      
      <div> 
          <label htmlFor="description" className="block text-sm font-medium text-content-2">Descripción</label> 
          <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm" placeholder="Ej. Válido de lunes a viernes..." /> 
      </div>
      
      {/* ✅ INPUT DE COSTO ADAPTADO */}
      <div> 
          <label htmlFor="points" className="block text-sm font-medium text-content-2">
              {loyaltyMode === 'cashback' ? 'Costo de la Recompensa ($)' : 'Puntos Requeridos'}
          </label> 
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                {loyaltyMode === 'cashback' ? (
                    <DollarSign className="h-4 w-4 text-content-3" />
                ) : (
                    <Star className="h-4 w-4 text-content-3" />
                )}
            </div>
            <input 
                type="number" 
                id="points" 
                name="points" 
                min="0" 
                step={loyaltyMode === 'cashback' ? "0.01" : "1"}
                value={formData.points} 
                onChange={handleInputChange} 
                required 
                className="block w-full pl-10 rounded-md border-line focus:border-blue-500 focus:ring-accent sm:text-sm" 
                placeholder={loyaltyMode === 'cashback' ? "0.00" : "0"}
            /> 
          </div>
          <p className="mt-1 text-xs text-content-3">
              {loyaltyMode === 'cashback' 
                ? 'Este monto se descontará del saldo del cliente al canjear.' 
                : 'Estos puntos se descontarán al canjear.'}
          </p>
      </div>

      <div>
        <ImageUpload
          title="Imagen de la Recompensa"
          description="Sube una imagen atractiva para el premio."
          accept="image/jpeg,image/png,image/webp"
          maxSizeMB={5}
          initialImageUrl={formData.imageUrl}
          onImageChange={(url) => {
            setFormData(prev => ({ ...prev, imageUrl: url }));
          }}
        />
        {!formData.imageUrl && (
             <p className="mt-1 text-xs text-red-600 dark:text-red-400">Se requiere una imagen para la recompensa.</p>
         )}
      </div>

      {sucursales.length >= 2 && (
        <div data-testid="recompensa-sucursales">
          <label className="flex items-center gap-1 text-sm font-medium text-content-2"><MapPin className="h-4 w-4" /> Disponible en</label>
          <div className="mt-2 flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-content">
              <input type="radio" name="rb-alcance" checked={!soloAlgunas} onChange={() => setSoloAlgunas(false)} data-testid="recompensa-todas" /> Todas las sucursales
            </label>
            <label className="flex items-center gap-2 text-sm text-content">
              <input type="radio" name="rb-alcance" checked={soloAlgunas} onChange={() => setSoloAlgunas(true)} data-testid="recompensa-solo" /> Solo en algunas
            </label>
          </div>
          {soloAlgunas && (
            <div className="mt-2 flex flex-wrap gap-2">
              {sucursales.map((b) => {
                const on = (formData.branchIds || []).includes(String(b.id));
                return (
                  <button key={b.id} type="button" onClick={() => toggleSucursal(String(b.id))} data-testid={`recompensa-suc-${b.id}`}
                    className={`rounded-full border px-3 py-1 text-xs ${on ? 'border-accent bg-accent/10 text-accent' : 'border-line text-content-2 hover:bg-surface-2'}`}>
                    {on ? '✓ ' : ''}{b.name}
                  </button>
                );
              })}
            </div>
          )}
          <p className="mt-1 text-xs text-content-3">
            {soloAlgunas ? 'Solo se podrá canjear en las sucursales marcadas. El saldo del cliente sigue siendo uno solo.' : 'Se canjea en cualquier sucursal.'}
          </p>
        </div>
      )}

      <div className="flex items-center"> 
          <input type="checkbox" id="isActive" name="isActive" checked={formData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-accent focus:ring-accent border-line rounded" /> 
          <label htmlFor="isActive" className="ml-2 block text-sm text-content">Recompensa activa (visible para clientes)</label> 
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0 mt-4">
        <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-line shadow-sm text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent">Cancelar</button>
        <button type="submit" disabled={loading || !formData.imageUrl || !formData.title || formData.points <= 0} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50">
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</> : <><Save className="h-4 w-4 mr-2" /> Guardar</>}
        </button>
      </div>
    </form>
  );
};

export default RewardForm;